import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { Grid, Skeleton } from '@mui/material';
import { Formik, Field } from 'formik';

import { useApi } from '../shared/context';
import {
	CerebroHeading,
	CerebroText,
	CerebroPrimaryButton,
	CerebroSelectableIcon,
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroForm,
} from '../shared/components';

import { ReactComponent as CashAndMarketables } from '../shared/icons/CashAndMarketables.svg';
import { ReactComponent as AccountsReceivables } from '../shared/icons/AccountsReceivable.svg';
import { ReactComponent as Inventory } from '../shared/icons/Inventory.svg';
import { ReactComponent as FurnitureFixtures } from '../shared/icons/FurnitureFixtures.svg';
import { ReactComponent as MachineryEquipment } from '../shared/icons/MachineryEquipment.svg';
import { ReactComponent as RollingStockVehicles } from '../shared/icons/RollingStockVehicles.svg';
import { ReactComponent as MarineVessels } from '../shared/icons/MarineVessels.svg';
import { ReactComponent as Helicopters } from '../shared/icons/Helicopters.svg';
import { ReactComponent as FixedAircraft } from '../shared/icons/FixedWingAircraft.svg';
import { ReactComponent as OwnerOccupiedRealEstate } from '../shared/icons/OwnerOccupiedRealEstate.svg';

import { getApiResponseData } from '../shared/utils/axios';
import { useSnackbar } from 'notistack';
import { DealRequestAllocation } from '../shared/models/DealRequestAllocation';
import { FormikUtils } from '../shared/utils';
import { QueryKeys, useDealRequestAllocationQuery, useNavigateToNextPage } from '../shared/hooks';
import { CerebroApiEndpoints } from '../shared/utils/api';

export const AcquisitionCollateralTypes: React.FC = () => {
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const navigateToNextPage = useNavigateToNextPage();

	const client = useQueryClient();
	const snackbar = useSnackbar();

	const allocationQuery = useDealRequestAllocationQuery(guid);

	const allocationMutation = useMutation(
		async ({ values }: DealAllocationMutationVariable) => {
			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);
				client.invalidateQueries(QueryKeys.Matching.Get(guid));
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	const requiredFields = getRequiredFields(allocationQuery.data);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Financials &gt; Acquisition &gt; Collateral Types</CerebroBreadcrumb>

			<CerebroHeading sx={{ marginBottom: 3 }}>Any additional assets to document?</CerebroHeading>

			<CerebroText sx={{ marginBottom: 8 }}>Select all that apply.</CerebroText>

			{allocationQuery.isLoading ? (
				<Skeleton height={400} />
			) : (
				<Formik
					initialValues={{
						acquisitionHasInventory:
							(allocationQuery.data?.acquisitionHasInventory ?? false) ||
							requiredFields.balanceSheet ||
							requiredFields.inventory,
						acquisitionHasOwnerOccupiedRealEstate:
							(allocationQuery.data?.acquisitionHasOwnerOccupiedRealEstate ?? false) ||
							requiredFields.realEstate,
						acquisitionHasFurnitureFixtures: allocationQuery.data?.acquisitionHasFurnitureFixtures ?? null,
						acquisitionHasMachineryEquipment:
							(allocationQuery.data?.acquisitionHasMachineryEquipment ?? false) ||
							requiredFields.equipment,
						acquisitionHasRollingStock: allocationQuery.data?.acquisitionHasRollingStock ?? null,
						acquisitionHasMarineVessels: allocationQuery.data?.acquisitionHasMarineVessels ?? null,
						acquisitionHasHelicopters: allocationQuery.data?.acquisitionHasHelicopters ?? null,
						acquisitionHasFixedWingAircraft: allocationQuery.data?.acquisitionHasFixedWingAircraft ?? null,
						acquisitionHasCashMarketables:
							(allocationQuery.data?.acquisitionHasCashMarketables ?? false) ||
							requiredFields.balanceSheet ||
							requiredFields.inventory,
						acquisitionHasAccountsReceivables:
							(allocationQuery.data?.acquisitionHasAccountsReceivables ?? false) ||
							requiredFields.balanceSheet ||
							requiredFields.inventory,
					}}
					onSubmit={FormikUtils.MutateOnSubmit(allocationMutation.mutate)}
				>
					{({ isSubmitting, handleSubmit }) => {
						return (
							<CerebroForm onSubmit={handleSubmit}>
								{requiredFields.any && (
									<>
										<CerebroHeading size="h3">Required Collateral Types</CerebroHeading>
										<br />

										<Grid container spacing={2} sx={{ marginBottom: 4 }}>
											{requiredFields.equipment && (
												<Grid item xs={12} md={6} lg={3}>
													<CerebroSelectableIcon
														name="requiredAcquisitionMachineryEquipment"
														sx={{ marginBottom: 2 }}
														stretch
														value={true}
														icon={() => <MachineryEquipment />}
													>
														Machinery &#38; Equipment
													</CerebroSelectableIcon>
												</Grid>
											)}
											{requiredFields.realEstate && (
												<Grid item xs={12} md={6} lg={3}>
													<CerebroSelectableIcon
														name="requiredAcquisitionRealEstate"
														sx={{ marginBottom: 2 }}
														stretch
														value={true}
														icon={() => <OwnerOccupiedRealEstate />}
													>
														Owner Occupied Real Estate
													</CerebroSelectableIcon>
												</Grid>
											)}
											{(requiredFields.balanceSheet || requiredFields.inventory) && (
												<>
													<Grid item xs={12} md={6} lg={3}>
														<CerebroSelectableIcon
															name="requiredAcquisitionCashMarketables"
															sx={{ marginBottom: 2 }}
															stretch
															value={true}
															icon={() => <CashAndMarketables />}
														>
															Cash &#38; Marketables
														</CerebroSelectableIcon>
													</Grid>
													<Grid item xs={12} md={6} lg={3}>
														<CerebroSelectableIcon
															name="requiredAcquisitionAccountsReceivables"
															sx={{ marginBottom: 2 }}
															stretch
															value={true}
															icon={() => <AccountsReceivables />}
														>
															Accounts Receivable
														</CerebroSelectableIcon>
													</Grid>
													<Grid item xs={12} md={6} lg={3}>
														<CerebroSelectableIcon
															name="requiredAcquisitionInventory"
															sx={{ marginBottom: 2 }}
															stretch
															value={true}
															icon={() => <Inventory />}
														>
															Inventory
														</CerebroSelectableIcon>
													</Grid>
												</>
											)}
										</Grid>
									</>
								)}

								{!requiredFields.balanceSheet && (
									<>
										<CerebroHeading size="h3">Balance Sheet</CerebroHeading>

										<br />

										<Grid container spacing={2} sx={{ marginBottom: 4 }}>
											<Grid item xs={12} md={6} lg={3}>
												<Field
													as={CerebroSelectableIcon}
													name="acquisitionHasCashMarketables"
													sx={{ marginBottom: 2 }}
													stretch
													disabled={isSubmitting}
													icon={() => <CashAndMarketables />}
												>
													Cash &#38; Marketables
												</Field>
											</Grid>
											<Grid item xs={12} md={6} lg={3}>
												<Field
													as={CerebroSelectableIcon}
													name="acquisitionHasAccountsReceivables"
													sx={{ marginBottom: 2 }}
													stretch
													disabled={isSubmitting}
													icon={() => <AccountsReceivables />}
												>
													Accounts Receivable
												</Field>
											</Grid>
										</Grid>
									</>
								)}

								<CerebroHeading size="h3">Tangible Assets</CerebroHeading>

								<br />

								<Grid container spacing={2} sx={{ marginBottom: 6 }}>
									{!requiredFields.inventory && (
										<Grid item xs={12} md={6} lg={3}>
											<Field
												as={CerebroSelectableIcon}
												name="acquisitionHasInventory"
												sx={{ marginBottom: 2 }}
												stretch
												disabled={isSubmitting}
												icon={() => <Inventory />}
											>
												Inventory
											</Field>
										</Grid>
									)}
									{!requiredFields.realEstate && (
										<Grid item xs={12} md={6} lg={3}>
											<Field
												as={CerebroSelectableIcon}
												name="acquisitionHasOwnerOccupiedRealEstate"
												sx={{ marginBottom: 2 }}
												stretch
												disabled={isSubmitting}
												icon={() => <OwnerOccupiedRealEstate />}
											>
												Owner Occupied Real Estate
											</Field>
										</Grid>
									)}
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="acquisitionHasFurnitureFixtures"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <FurnitureFixtures />}
										>
											Furniture / Fixtures
										</Field>
									</Grid>
									{!requiredFields.equipment && (
										<Grid item xs={12} md={6} lg={3}>
											<Field
												as={CerebroSelectableIcon}
												name="acquisitionHasMachineryEquipment"
												sx={{ marginBottom: 2 }}
												stretch
												disabled={isSubmitting}
												icon={() => <MachineryEquipment />}
											>
												Machinery &#38; Equipment
											</Field>
										</Grid>
									)}
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="acquisitionHasRollingStock"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <RollingStockVehicles />}
										>
											Rolling Stock / Vehicles
										</Field>
									</Grid>
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="acquisitionHasMarineVessels"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <MarineVessels />}
										>
											Marine Vessels
										</Field>
									</Grid>
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="acquisitionHasHelicopters"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <Helicopters />}
										>
											Helicopters
										</Field>
									</Grid>
									<Grid item xs={12} md={6} lg={3}>
										<Field
											as={CerebroSelectableIcon}
											name="acquisitionHasFixedWingAircraft"
											sx={{ marginBottom: 2 }}
											stretch
											disabled={isSubmitting}
											icon={() => <FixedAircraft />}
										>
											Fixed Aircraft
										</Field>
									</Grid>
								</Grid>

								<Grid
									container
									sx={{ paddingBottom: 2 }}
									justifyContent={{ xs: 'center', md: 'flex-start' }}
								>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Save &amp; Continue
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			)}
		</CerebroPageContainer>
	);
};

function getRequiredFields(allocation?: DealRequestAllocation) {
	const fields = {
		any: false,
		balanceSheet: false,
		inventory: false,
		realEstate: false,
		equipment: false,
	};

	if (allocation === undefined) return fields;

	const isAcquisitionTypeThatDataIsRequiredFor =
		allocation.dealType === 'AcquisitionManagementBuyout' || allocation.dealType === 'AcquisitionPartnerBuyout';

	if (!isAcquisitionTypeThatDataIsRequiredFor) return fields;

	fields.balanceSheet = allocation.financeExpensesOrGrowth === true || allocation.purchaseWorkingCapital === true;
	fields.inventory = fields.balanceSheet;

	fields.realEstate = allocation.purchaseRealEstate === true;
	fields.equipment = allocation.purchaseEquipment === true;

	fields.any = fields.balanceSheet || fields.inventory || fields.realEstate || fields.equipment;

	return fields;
}

interface AcquisitionCollateralTypesFormValues {
	acquisitionHasInventory: boolean;
	acquisitionHasOwnerOccupiedRealEstate: boolean;
	acquisitionHasFurnitureFixtures: boolean | null;
	acquisitionHasMachineryEquipment: boolean;
	acquisitionHasRollingStock: boolean | null;
	acquisitionHasMarineVessels: boolean | null;
	acquisitionHasHelicopters: boolean | null;
	acquisitionHasFixedWingAircraft: boolean | null;
	acquisitionHasCashMarketables: boolean;
	acquisitionHasAccountsReceivables: boolean;
}

type DealAllocationMutationVariable = FormikUtils.MutateOnSubmitVariables<AcquisitionCollateralTypesFormValues>;
